/**
 * Closers Page - Closer performance metrics and data
 *
 * Shows:
 * - Key metrics (Cash Collected, Revenue, MRR, New Cash)
 * - Closer Commission (10% of cash collected)
 * - Performance stats (Show Rate, Close Rate, Calls Due/Taken/Closed)
 * - Average stats (Avg Cash/Call, Close, Application, Opt-in)
 * - Close Rate Trend chart
 * - Deal Status Breakdown chart
 * - Closer Performance table
 *
 * Renders via the DashboardShell widget system.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { MetricCardSkeleton, StatCardSkeleton, FilterBar } from "../components";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useSession } from "../auth/client";
import { DashboardDataProvider } from "../context/DashboardContext";
import { DashboardShell } from "../components/dashboard/DashboardShell";
import { CLOSERS_DEFAULT } from "../components/dashboard/defaults";
import type { DashboardData } from "../components/dashboard/types";

interface DashboardMetrics {
  totalCashCollected: number;
  totalRevenue: number;
  closeRate: number;
  showRate: number;
  callsDue: number;
  callsTaken: number;
  callsClosed: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
  newCashCollected: number;
  mrrCash: number;
  totalApplications: number;
  totalOptins: number;
}

interface TrendDataPoint {
  date: string;
  value: number;
}

interface OutcomeItem {
  _id: string;
  count: number;
  cash: number;
}

interface CloserPerformanceItem {
  name: string;
  totalCash: number;
  callCount: number;
  avgCashPerCall: number;
}

interface FilterOption {
  value: string;
  label: string;
  allValues?: string[];
}

function optionMatchesValue(options: FilterOption[], value: string): boolean {
  const valueParts = String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return options.some((option) => {
    const optionParts = String(option.value || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    const extraParts = Array.isArray(option.allValues) ? option.allValues : [];

    return (
      option.value === value ||
      option.label === value ||
      optionParts.includes(value) ||
      extraParts.includes(value) ||
      (valueParts.length > 0 && valueParts.every((part) => optionParts.includes(part) || extraParts.includes(part)))
    );
  });
}

function normalizeToFilterOptions(data: unknown): Array<{ value: string; label: string }> {
  if (!Array.isArray(data)) return [];
  return data.map((item: unknown) => {
    if (typeof item === "string") return { value: item, label: item };
    if (item && typeof item === "object" && "value" in item) {
      const obj = item as FilterOption;
      return { ...obj, value: obj.value, label: obj.label || obj.value };
    }
    return { value: String(item), label: String(item) };
  });
}

export function Closers() {
  const { data: session } = useSession();
  const { dateRange: globalDateRange, formatForApi } = useDateRange();
  const {
    dateField,
    closers,
    setters,
    sources,
    contents,
    countries,
    funnels,
    leadType,
    callOutcome,
    offer,
    setDateField,
    setLeadType,
    setCallOutcome,
    offers,
    setOffers,
    // Array setters for clearing invalid filters
    setClosers,
    setSetters,
    setFunnels,
    setContents,
    setCountries,
    setSources,
    clearAllFilters,
  } = useFilters();

  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [cashTrend, setCashTrend] = useState<TrendDataPoint[]>([]);
  const [outcomeBreakdown, setOutcomeBreakdown] = useState<OutcomeItem[]>([]);
  const [closerPerformance, setCloserPerformance] = useState<CloserPerformanceItem[]>([]);
  const [filterOptions, setFilterOptions] = useState<{
    closers: FilterOption[];
    setters: FilterOption[];
    funnels: FilterOption[];
    contents: FilterOption[];
    countries: FilterOption[];
    sources: FilterOption[];
    offers: FilterOption[];
  }>({
    closers: [],
    setters: [],
    funnels: [],
    contents: [],
    countries: [],
    sources: [],
    offers: [],
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();

      // Add date range
      const { startDate, endDate } = formatForApi();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);

      // Add filters
      if (dateField) params.set("dateField", dateField);
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));
      if (sources.length > 0) params.set("source", sources.join(","));
      if (contents.length > 0) params.set("content", contents.join(","));
      if (countries.length > 0) params.set("country", countries.join(","));
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (leadType) params.set("leadType", leadType);
      if (callOutcome) params.set("callOutcome", callOutcome);
      if (offer && offer !== "all") params.set("offer", offer);
      const selectedOffers = offers.filter((o: string) => o !== "all");
      if (offer === "all" && selectedOffers.length > 0) params.set("offer", selectedOffers.join(","));

      params.set("closedOnly", "true");

      const response = await fetch(`/api/dashboard/summary?${params.toString()}`);
      const result = await response.json();

      if (result.success) {
        const { data } = result;
        setMetrics(data.metrics);
        setCashTrend(data.cashTrend || []);
        setOutcomeBreakdown(data.outcomeBreakdown || []);
        setCloserPerformance(data.closerPerformance || []);
        setFilterOptions({
          closers: normalizeToFilterOptions(data.filterOptions?.closers),
          setters: normalizeToFilterOptions(data.filterOptions?.setters),
          funnels: normalizeToFilterOptions(data.filterOptions?.funnels),
          contents: normalizeToFilterOptions(data.filterOptions?.contents),
          countries: normalizeToFilterOptions(data.filterOptions?.countries),
          sources: normalizeToFilterOptions(data.filterOptions?.sources),
          offers: normalizeToFilterOptions(data.filterOptions?.offers),
        });
      }
    } catch (error) {
      console.error("Failed to fetch closer data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [globalDateRange, formatForApi, dateField, closers, setters, sources, contents, countries, funnels, leadType, callOutcome, offer, offers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-remove invalid filters when available options change
  useEffect(() => {
    // Skip if filterOptions haven't loaded yet
    if (!filterOptions.closers.length && !filterOptions.setters.length && !filterOptions.funnels.length) {
      return;
    }

    if (closers.length > 0 && filterOptions.closers.length > 0) {
      const validClosers = closers.filter((value) => optionMatchesValue(filterOptions.closers, value));
      if (validClosers.length !== closers.length) setClosers(validClosers);
    }

    if (setters.length > 0 && filterOptions.setters.length > 0) {
      const validSetters = setters.filter((value) => optionMatchesValue(filterOptions.setters, value));
      if (validSetters.length !== setters.length) setSetters(validSetters);
    }

    if (funnels.length > 0 && filterOptions.funnels.length > 0) {
      const validFunnels = funnels.filter((value) => optionMatchesValue(filterOptions.funnels, value));
      if (validFunnels.length !== funnels.length) setFunnels(validFunnels);
    }

    if (contents.length > 0 && filterOptions.contents.length > 0) {
      const validContents = contents.filter((value) => optionMatchesValue(filterOptions.contents, value));
      if (validContents.length !== contents.length) setContents(validContents);
    }

    if (countries.length > 0 && filterOptions.countries.length > 0) {
      const validCountries = countries.filter((value) => optionMatchesValue(filterOptions.countries, value));
      if (validCountries.length !== countries.length) setCountries(validCountries);
    }

    if (sources.length > 0 && filterOptions.sources.length > 0) {
      const validSources = sources.filter((value) => optionMatchesValue(filterOptions.sources, value));
      if (validSources.length !== sources.length) setSources(validSources);
    }
  }, [filterOptions, closers, setters, funnels, contents, countries, sources, setClosers, setSetters, setFunnels, setContents, setCountries, setSources]);

  // Get greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  };

  // Handle clear all filters
  const handleClearAll = () => {
    clearAllFilters();
  };

  // Prepare deal status breakdown from outcome data (for pie chart)
  const dealStatusData = useMemo(() =>
    outcomeBreakdown
      .filter((o) => o._id && o._id.trim() !== "")
      .slice(0, 6)
      .map((o) => ({
        name: o._id,
        value: o.count,
      })),
    [outcomeBreakdown]
  );

  // Create close rate trend data (simulate based on cash trend dates)
  const closeRateTrend = useMemo(() =>
    cashTrend.map((point) => ({
      date: point.date,
      value: metrics?.closeRate ? Math.max(0, Math.min(100, metrics.closeRate + (Math.random() - 0.5) * 10)) : 0,
    })),
    [cashTrend, metrics?.closeRate]
  );

  // --- Map API data into DashboardData context format ---
  const dashboardData: DashboardData = useMemo(() => ({
    page: "closers",
    metrics: {
      totalCashCollected: metrics?.totalCashCollected ?? 0,
      totalRevenue: metrics?.totalRevenue ?? 0,
      closeRate: metrics?.closeRate ?? 0,
      showRate: metrics?.showRate ?? 0,
      callsDue: metrics?.callsDue ?? 0,
      callsTaken: metrics?.callsTaken ?? 0,
      callsClosed: metrics?.callsClosed ?? 0,
      avgCashPerCall: metrics?.avgCashPerCall ?? 0,
      avgCashPerClose: metrics?.avgCashPerClose ?? 0,
      avgCashPerApplication: metrics?.avgCashPerApplication ?? 0,
      avgCashPerOptin: metrics?.avgCashPerOptin ?? 0,
      newCashCollected: metrics?.newCashCollected ?? 0,
      mrrCash: metrics?.mrrCash ?? 0,
      totalApplications: metrics?.totalApplications ?? 0,
      totalOptins: metrics?.totalOptins ?? 0,
    },
    chartData: {
      closeRateTrend: closeRateTrend,
      outcomeBreakdown: dealStatusData,
    },
    chartInteractions: {
      outcomeBreakdown: {
        onSelect: (value) => {
          if (!value.trim()) return;
          setCallOutcome(callOutcome === value ? "" : value);
        },
        isActive: (value) => callOutcome === value,
      },
    },
    tableData: {
      closerPerformance: closerPerformance,
    },
    filterOptions: {
      closers: filterOptions.closers,
      setters: filterOptions.setters,
      funnels: filterOptions.funnels,
      contents: filterOptions.contents,
      countries: filterOptions.countries,
      sources: filterOptions.sources,
      offers: filterOptions.offers,
    },
    rawData: {},
    isLoading,
    refetch: fetchData,
  }), [
    metrics, closeRateTrend, dealStatusData, closerPerformance,
    filterOptions, isLoading, fetchData, callOutcome, setCallOutcome,
  ]);

  // Only show skeleton on initial load, not when filtering (keep old data visible)
  if (isLoading && !metrics) {
    return (
      <div className="space-y-4 md:space-y-6">
        {/* Metric Cards Skeleton */}
        <div className="grid gap-4 md:gap-6 grid-cols-2 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <MetricCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Commission Skeleton */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative overflow-hidden bg-gradient-to-br from-indigo-500/20 via-indigo-500/10 to-transparent border-indigo-500/20 border rounded-2xl p-4 md:p-6"
        >
          <div className="h-4 w-32 rounded bg-muted animate-pulse mb-3" />
          <div className="h-10 w-40 rounded bg-muted animate-pulse" />
        </motion.div>

        {/* Stats Skeleton */}
        <div className="grid gap-3 md:gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Chart Skeletons */}
        <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2">
          {[0, 1].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.1 }}
              className="relative overflow-hidden bg-card rounded-xl border border-border p-4 md:p-6"
            >
              <div className="h-5 w-40 rounded bg-muted animate-pulse mb-4" />
              <div className="h-[200px] md:h-[300px] rounded-lg bg-muted/30 animate-pulse" />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // --- Header JSX ---
  const headerContent = (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="flex items-center justify-between"
    >
      <motion.h1
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="text-lg md:text-2xl font-bold tracking-tight"
      >
        {getGreeting()}
      </motion.h1>
    </motion.div>
  );

  // --- Filter Bar JSX ---
  const filterBarContent = (
    <FilterBar
      showDateFieldToggle={true}
      dateField={dateField as "timestamp" | "dateCallWasTaken"}
      onDateFieldChange={(v) => setDateField(v)}
      filters={[
        {
          key: "closer",
          label: "Select Closer",
          options: filterOptions.closers,
          value: closers,
          onChange: (v) => setClosers(v as string[]),
          multiple: true,
        },
        {
          key: "setter",
          label: "Select Setter",
          options: filterOptions.setters,
          value: setters,
          onChange: (v) => setSetters(v as string[]),
          multiple: true,
        },
        {
          key: "source",
          label: "Select Source",
          options: filterOptions.sources,
          value: sources,
          onChange: (v) => setSources(v as string[]),
          multiple: true,
        },
        {
          key: "content",
          label: "Select Content",
          options: filterOptions.contents,
          value: contents,
          onChange: (v) => setContents(v as string[]),
          multiple: true,
        },
        {
          key: "funnel",
          label: "Select Funnel",
          options: filterOptions.funnels,
          value: funnels,
          onChange: (v) => setFunnels(v as string[]),
          multiple: true,
        },
        ...(offer === "all" && filterOptions.offers.length > 0
          ? [{
              key: "offers",
              label: "Select Offers",
              options: filterOptions.offers,
              value: offers.filter((o: string) => o !== "all"),
              onChange: (v: string | string[]) => setOffers(["all", ...(v as string[])]),
              multiple: true,
            }]
          : []),
      ]}
      onClearAll={handleClearAll}
    />
  );

  return (
    <DashboardDataProvider value={dashboardData}>
      <DashboardShell
        page="closers"
        defaultLayout={CLOSERS_DEFAULT}
        filterBar={filterBarContent}
        header={headerContent}
      />
    </DashboardDataProvider>
  );
}
